import { forwardRef, useEffect, useImperativeHandle, useState } from "react"

export interface IGenericModalHandle {
    onClose: (e) => void
}

interface IGenericModalProps {
    children?: React.ReactNode,
    primaryButton?: JSX.Element,
    secondaryButton?: JSX.Element,
    buttonPlacement?: "center" | "normal",
    maxWidth?: "max-w-xs" | "max-w-sm" | "max-w-md" | "max-w-lg" | "max-w-xl" | "max-w-2xl" | "max-w-3xl" | "max-w-4xl" | "max-w-5xl" | "max-w-6xl" | "max-w-7xl" | "max-w-full" | "max-w-fit"
    modalTitle: string,
    onClose?: React.MouseEventHandler<HTMLButtonElement>
    isScrollingEnabled?: boolean,
    disableCloseOnClickOutside?: boolean
}

export const GenericModal = forwardRef<IGenericModalHandle, IGenericModalProps>((props, ref) => {

    const { isScrollingEnabled = true } = props
    const [isVisible, setIsVisible] = useState(true)
    const [footer, setFooter] = useState(null)

    const onClose = (e) => {
        setIsVisible(false)
        setTimeout(() => {
            if (props.onClose) {
                props.onClose(e)
            }
        }, 200)
    }

    useImperativeHandle(ref, () => ({
        onClose: (e) => {
            setIsVisible(false)
            setTimeout(() => {
                if (props.onClose) {
                    props.onClose(e)
                }
            }, 200)
        }
    }))


    useEffect(() => {
        if (props.primaryButton || props.secondaryButton) {
            if (props.buttonPlacement == 'center') {
                setFooter(
                    <div className="flex flex-row justify-center p-4 border-t border-slate-200">
                        {props.secondaryButton}
                        {props.primaryButton}
                    </div>
                )
            } else {
                setFooter(
                    <div className="flex flex-row justify-between p-4 border-t border-slate-200">
                        <div className="justify-self-start">
                            {
                                props.secondaryButton
                            }
                        </div>
                        <div className="justify-self-end">
                            {
                                props.primaryButton
                            }
                        </div>
                    </div>
                )
            }
        }
    }, [props.buttonPlacement, props.primaryButton, props.secondaryButton,])

    return (
        <div className={`fixed inset-0 z-50 overflow-y-auto transition-opacity duration-200 ${!isVisible ? "hidden" : ""}`}>
            {
                // Modal background
            }
            <div className="fixed inset-0">
                <div
                    className="absolute inset-0 opacity-75 bg-slate-800"
                ></div>
            </div>

            <div className={`transform origin-bottom transition-transform duration-300 md:duration-400 ${isVisible ? "ease-out translate-y-0" : "ease-in translate-y-full"}`}>
                <div className={`min-h-screen flex flex-col items-center justify-center`}
                    onClick={!props.disableCloseOnClickOutside ? onClose : undefined}
                >
                    {
                        // Modal window
                    }
                    <div
                        className={`overflow-hidden grow md:grow-0 w-full ${props.maxWidth || "max-w-lg"} bg-white rounded-lg shadow-xl`}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="modal-headline"
                        onClick={(e) => { e.stopPropagation() }}
                    >
                        <div className={`${isScrollingEnabled ? "max-h-screen" : ""} flex flex-col bg-white`}>
                            {
                                // Modal header
                            }
                            <div className="flex flex-row p-2 md:p-4 border-b border-slate-200">
                                {
                                    // Close button
                                }
                                <button
                                    className="flex rounded-full idtn-btn idtn-btn-naked justify-self-start "
                                    aria-haspopup="true"
                                    onClick={onClose}
                                >
                                    <span className="sr-only">Close {props.modalTitle}</span>
                                    <div className="inline-flex items-center justify-center text-lg font-bold align-middle w-7 h-7 md:h-8 md:w-8">
                                        <i className="fa-solid fa-xmark"></i>
                                    </div>
                                </button>
                                {
                                    // Modal title
                                }
                                <div className="flex flex-grow justify-self-center">
                                    <h3
                                        className="self-center flex-grow text-lg font-medium tracking-tight text-center text-gray-900"
                                        id="modal-headline"
                                    >
                                        {props.modalTitle}
                                    </h3>

                                </div>
                                {
                                    // Spacer to keep modal title in the middle
                                }
                                <div className="w-7 h-7 md:h-8 md:w-8"></div>

                            </div>
                            {
                                // Modal content
                            }
                            <div className={`${isScrollingEnabled ? "overflow-y-auto overflow-x-hidden" : "overflow-hidden"} p-2 md:p-4`}>
                                <div className="text-sm leading-5 text-gray-500 ">
                                    {
                                        props.children
                                    }
                                </div>
                            </div>
                            {
                                // Modal footer
                            }
                            {footer}

                        </div>


                    </div>
                </div>
            </div>
        </div >
    )
})